import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Certificate } from '@app/_models/certificate';
import { ReportSearch } from '@app/_models/reportSearch';
import { RsaProvided } from '@app/_models/rsaProvided';
import { AlertService, AccountService } from '@app/_services';
import { CertificateService } from '@app/_services/certificate.service';
import { SearchService } from '@app/_services/search.service';
import { ConfigDataService } from '@app/_services/config-data.service';
import { RsaServiceData } from '@app/_services';
import { first, debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-rsa-service-provided',
  templateUrl: './rsa-service-provided.component.html',
  styleUrls: ['./rsa-service-provided.component.less']
})
export class RsaServiceProvidedComponent implements OnInit, OnDestroy {
  // Form subscriptions
  private stateChangeSubscription: Subscription;
  private cityChangeSubscription: Subscription;
  
  certificateReportForm: UntypedFormGroup;
  rsaProvidedForm: UntypedFormGroup;
  submitted=false;
  loading= false;
  reportSearch = new ReportSearch();
  certificates : Certificate[];
  selectedCertificate: Certificate;
  currentCertificateIndex = 0;
  totalCertificate = 0;
  isCertificateExpired=false;
  showRsaForm: boolean;
  selectRsaProvide: RsaProvided;
  providerList: string[] = [];
  stateList: string[] = [];
  stateCityList: any[] = [];
  cityList: string[] = [];
  rsaProvidedList: RsaProvided[] = [];
  newHistoryCommentControl = new UntypedFormControl('');
  selectedProviderContactNumber: string = ''; // To store the contact number of the selected provider
  providerDetailsMap: { [key: string]: any } = {}; // Cache for provider details by provider name
  today: Date = new Date(); // Current date for the UI
  currentDateTimeIST: string; // Current date/time in India Standard Time
  
  // Offset for India Standard Time (IST): UTC+5:30
  readonly IST_HOURS_OFFSET = 5;
  readonly IST_MINUTES_OFFSET = 30;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private searchService: SearchService, 
    private alertService: AlertService,
    private certificateService:CertificateService,
    private configDataService: ConfigDataService,
    private router:Router,
    private accountService: AccountService,
    private rsaServiceData: RsaServiceData // Inject new service
  ) { 
    // Initialize IST date/time
    this.currentDateTimeIST = this.getISTDateTime();
  }

  // Helper function to get current date/time in IST
  getISTDateTime(): string {
    const now = new Date();
    const utcDate = new Date(now.getTime() + now.getTimezoneOffset() * 60000); // Convert to UTC
    const istDate = new Date(utcDate.getTime() + (this.IST_HOURS_OFFSET * 3600000) + (this.IST_MINUTES_OFFSET * 60000));
    
    // Format as YYYY-MM-DDTHH:MM (suitable for datetime-local input)
    return istDate.toISOString().slice(0, 16);
  }

  ngOnInit(): void {
    // Update IST date/time
    this.currentDateTimeIST = this.getISTDateTime();

    this.certificateReportForm = this.formBuilder.group({
      certificateNumber: [''],
      chasisNumber:[''],
      contactNumber:[''],
          });
    
    this.rsaProvidedForm = this.formBuilder.group({
      // Customer Data
      state: ['', Validators.required],
      city: ['', Validators.required],
      callerName:[''],
      callbackNumber:[''],
      // RSA Provider & Dealership Data
      dealershipLocation: ['', Validators.required],
      provider: ['', Validators.required],
      // If provider is 'Other', show reimbursementAmount and reimbursementComment
      reimbursementAmount: [''],
      reimbursementComment: [''],
      // Financials
      distance: ['', [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      serviceCost: ['', [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      serviceStatus: ['In progress', Validators.required],
      paidBy: ['', Validators.required],
      // Service meta using IST timezone
      rsaProvidedDate:[this.getISTDateTime()],
      rsaProvideInstanceNumber:[0],
      certificateNumber:[''], // will be set on submit
      // History
      history: [[]]
    });

    // Load state/city list for dropdowns
    this.configDataService.getStateCity().subscribe((data: any[]) => {
      this.stateCityList = data;
      this.stateList = Array.from(new Set(data.map(x => x.state))).sort();
    });
  }
  get f() { return this.certificateReportForm.controls; }
  get rsaF(){return this.rsaProvidedForm.controls;}

  // Methods for new subcomponents
  onSearchSubmitted(searchData: any) {
    console.log("Search Data: ", searchData);
    this.submitted = true;
    this.loading = true;
    this.alertService.clear();
    
    this.reportSearch = new ReportSearch();
    this.reportSearch.certificateNumber = searchData.certificateNumber;
    this.reportSearch.chasisNumber = searchData.chasisNumber;
    this.reportSearch.contactNumber = searchData.contactNumber;
    
    if (this.reportSearch.certificateNumber || this.reportSearch.chasisNumber || this.reportSearch.contactNumber) {
      this.reportSearch.issueDateFrom = "2020-01-01";
      this.reportSearch.issueDateTo = new Date().toISOString();
      console.log(this.reportSearch)
      this.searchService.getRenewCertificateList(this.reportSearch)
        .pipe(first())
        .subscribe({
          next: (x) => {
            this.certificates = x;
            this.totalCertificate = this.certificates.length;
            if (this.certificates.length === 0) {
              this.alertService.error("No Records Found");
            } else {
              this.selectedCertificate = this.certificates[0];
              this.loadRsaProvidedAndValidate();
            }
            this.loading = false;
          },
          error: error => {
            this.alertService.error(error);
            this.loading = false;
          }
        });
    } else {
      this.alertService.error('No Search Criteria provided');
      this.loading = false;
    }
  }
  
  onSearchReset() {
    this.certificates = [];
    this.totalCertificate = 0;
    this.selectedCertificate = null;
    this.rsaProvidedList = [];
    this.showRsaForm = false;
    this.isCertificateExpired = false;
  }

  onSubmit(){
    this.submitted = true;
    this.loading = true;
    this.alertService.clear();
    this.reportSearch = new ReportSearch();

    this.reportSearch.certificateNumber = this.certificateReportForm.get('certificateNumber').value;
    this.reportSearch.chasisNumber = this.certificateReportForm.get('chasisNumber').value;
    this.reportSearch.contactNumber = this.certificateReportForm.get('contactNumber').value;
    
    if (this.reportSearch.certificateNumber || this.reportSearch.chasisNumber || this.reportSearch.contactNumber) {
      this.reportSearch.issueDateFrom = "2020-01-01";
      this.reportSearch.issueDateTo = new Date().toISOString();
      this.searchService.getRenewCertificateList(this.reportSearch)
        .pipe(first())
        .subscribe({
          next: (x) => {
            this.certificates = x;
            this.totalCertificate = this.certificates.length;
            if (this.certificates.length === 0) {
              this.alertService.error("No Records Found");
            } else {
              this.selectedCertificate = this.certificates[0];
              this.validCertificate();
            }
            this.loading = false;
          },
          error: error => {
            this.alertService.error(error);
            this.loading = false;
          }
        });
    } else {
      this.alertService.error('No Search Criteria provided');
      this.loading = false;
    }
  }
previousAction()
{
  this.currentCertificateIndex = this.currentCertificateIndex - 1;
  this.selectedCertificate = this.certificates[this.currentCertificateIndex];
  this.loadRsaProvidedAndValidate();
}
nextAction()
{
  console.log(this.currentCertificateIndex);
  this.currentCertificateIndex = this.currentCertificateIndex + 1;
  this.selectedCertificate = this.certificates[this.currentCertificateIndex];
  this.loadRsaProvidedAndValidate();
}
pageSelected(i:number)
{
  // Don't subtract 1 from the index, as the pagination component already sends the correct 0-based index
  this.currentCertificateIndex = i;
  this.selectedCertificate = this.certificates[this.currentCertificateIndex];
  this.loadRsaProvidedAndValidate();
}
loadRsaProvidedAndValidate() {
  this.certificateService.getRsaProvidedByCertificateNumber(this.selectedCertificate.certificateNumber)
    .pipe(first())
    .subscribe({
      next: (rsaProvidedList) => {
        this.rsaProvidedList = rsaProvidedList || [];
        this.selectedCertificate.rsaProvidedInfo = rsaProvidedList || [];
        this.selectedCertificate.rsaProvidedCount = this.selectedCertificate.rsaProvidedInfo.length;
        this.validCertificate();
      },
      error: error => {
        this.selectedCertificate.rsaProvidedInfo = [];
        this.selectedCertificate.rsaProvidedCount = 0;
        this.validCertificate();
        this.alertService.error('Failed to load RSA Provided Services for this certificate.');
      }
    });
}
validCertificate()
{
  this.isCertificateExpired = false;
  this.selectedCertificate.rsaProvidedCount = this.selectedCertificate.rsaProvidedCount ? this.selectedCertificate.rsaProvidedCount : 0;
  this.selectedCertificate.rsaNumberOfServices = this.selectedCertificate.rsaNumberOfServices ? this.selectedCertificate.rsaNumberOfServices : 999;
  this.selectedCertificate.startDate = new Date(this.selectedCertificate.startDate).toISOString().split('T')[0];
  this.selectedCertificate.endDate = new Date(this.selectedCertificate.endDate).toISOString().split('T')[0];
  this.selectedCertificate.rsaProvidedInfo = this.selectedCertificate.rsaProvidedInfo ? this.selectedCertificate.rsaProvidedInfo : [];

  if (new Date(this.selectedCertificate.endDate) < new Date())
  {
    this.isCertificateExpired = true;
  }
  this.isCertificateExpired = Number(this.selectedCertificate.rsaNumberOfServices) <= Number(this.selectedCertificate.rsaProvidedCount) ? true : this.isCertificateExpired;
}

enterRSAClick() {
    this.submitted = false;
    this.showRsaForm = true;
    this.selectedProviderContactNumber = ''; // Reset the provider contact number
    this.providerDetailsMap = {}; // Reset provider cache
    this.providerList = []; // Reset provider list
    
    // Create the form again to clear any previous value change subscriptions
    this.rsaProvidedForm = this.formBuilder.group({
      // Customer Data
      state: ['', Validators.required],
      city: ['', Validators.required],
      callerName:[''],
      callbackNumber:[''],
      // RSA Provider & Dealership Data
      dealershipLocation: ['', Validators.required],
      provider: ['', Validators.required],
      // If provider is 'Other', show reimbursementAmount and reimbursementComment
      reimbursementAmount: [''],
      reimbursementComment: [''],
      // Financials
      distance: ['', [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      serviceCost: ['', [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      serviceStatus: ['In progress', Validators.required],
      paidBy: ['', Validators.required],
      // Service meta using IST timezone
      rsaProvidedDate:[this.getISTDateTime()],
      rsaProvideInstanceNumber:[0],
      certificateNumber:[''], // will be set on submit
      // History
      history: [[]]
    });
    
    // Prepopulate state, city, and callbackNumber from certificate
    this.rsaProvidedForm.patchValue({
      state: this.selectedCertificate.state || '',
      city: this.selectedCertificate.city || '',
      callerName: ((this.selectedCertificate.firstName || '') + ' ' + (this.selectedCertificate.lastName || '')).trim(),
      callbackNumber: this.selectedCertificate.contactNumber || '',
      rsaProvidedDate: this.getISTDateTime() // Use IST time instead of GMT
    });
    
    // First unsubscribe from any existing subscriptions to prevent memory leaks
    if (this.stateChangeSubscription) {
      this.stateChangeSubscription.unsubscribe();
    }
    if (this.cityChangeSubscription) {
      this.cityChangeSubscription.unsubscribe();
    }
    
    // Set up value change subscriptions for state and city fields
    this.stateChangeSubscription = this.rsaProvidedForm.get('state').valueChanges
      .pipe(
        // Add a small debounce to avoid too many API calls during rapid changes
        debounceTime(300)
      )
      .subscribe(state => {
        if (state) {
          this.onStateChange(state);
        } else {
          // Clear provider list if state is empty
          this.providerList = [];
          this.providerDetailsMap = {};
        }
      });
    
    this.cityChangeSubscription = this.rsaProvidedForm.get('city').valueChanges
      .pipe(
        // Add a small debounce to avoid too many API calls during rapid changes
        debounceTime(300)
      )
      .subscribe(city => {
        if (city) {
          this.onCityChange(city);
        } else {
          // If city is cleared, fall back to state providers
          const state = this.rsaProvidedForm.get('state').value;
          if (state) {
            this.onStateChange(state);
          }
        }
      });
    
    // Ensure cityList is populated if state is present
    if (this.selectedCertificate.state) {
      this.onStateChange(this.selectedCertificate.state);
    }
    
    // Load provider list for selected city, fallback to state if none
    const city = this.selectedCertificate.city;
    const state = this.selectedCertificate.state;
    if (city) {
      this.configDataService.getRsaProviderByCity(city).pipe(first()).subscribe({
        next: (providers) => {
          if (providers && providers.length > 0) {
            // Cache provider details for quick lookup later
            providers.forEach(p => {
              const displayName = `${p.providerName} (${p.city})`;
              this.providerDetailsMap[displayName] = p;
            });
            this.providerList = providers.map(p => `${p.providerName} (${p.city})`);
          } else if (state) {
            this.configDataService.getRsaProviderByState(state).pipe(first()).subscribe({
              next: (stateProviders) => {
                // Cache provider details from state
                stateProviders.forEach(p => {
                  const displayName = `${p.providerName} (${p.city})`;
                  this.providerDetailsMap[displayName] = p;
                });
                this.providerList = (stateProviders || []).map(p => `${p.providerName} (${p.city})`);
              },
              error: () => {
                this.providerList = [];
              }
            });
          } else {
            this.providerList = [];
          }
        },
        error: () => {
          this.providerList = [];
        }
      });
    } else if (state) {
      this.configDataService.getRsaProviderByState(state).pipe(first()).subscribe({
        next: (stateProviders) => {
          // Cache provider details from state
          stateProviders.forEach(p => {
            const displayName = `${p.providerName} (${p.city})`;
            this.providerDetailsMap[displayName] = p;
          });
          this.providerList = (stateProviders || []).map(p => `${p.providerName} (${p.city})`);
        },
        error: () => {
          this.providerList = [];
        }
      });
    } else {
      this.providerList = [];
    }
  }
onProviderChange(provider: string) {
    // Reset contact number when provider changes
    this.selectedProviderContactNumber = '';
    
    if (provider === 'Other') {
      // Show reimbursement fields and set validators
      this.rsaProvidedForm.get('reimbursementAmount').setValidators([Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]);
      this.rsaProvidedForm.get('reimbursementComment').setValidators([Validators.required]);
    } else if (provider) {
      // Clear reimbursement validators
      this.rsaProvidedForm.get('reimbursementAmount').clearValidators();
      this.rsaProvidedForm.get('reimbursementComment').clearValidators();
      
      // Extract provider name and city from the selected provider string
      const providerName = provider.split(' (')[0];
      const providerCity = provider.split(' (')[1]?.replace(')', '') || '';
      
      // Look for the provider in our cached providers list
      const matchingProvider = this.providerDetailsMap[provider];
      if (matchingProvider) {
        this.selectedProviderContactNumber = matchingProvider.contactNumber;
      } else {
        // Fallback to API call if not found in cache
        if (providerCity) {
          this.configDataService.getRsaProviderByCity(providerCity).pipe(first()).subscribe({
            next: (providers) => {
              const foundProvider = providers?.find(p => p.providerName === providerName);
              if (foundProvider) {
                this.selectedProviderContactNumber = foundProvider.contactNumber;
                // Add to our cache
                this.providerDetailsMap[provider] = foundProvider;
              }
            }
          });
        } else {
          // If city can't be determined, search by provider name
          this.configDataService.getRsaProviderByName(providerName).pipe(first()).subscribe({
            next: (providers) => {
              if (providers && providers.length > 0) {
                this.selectedProviderContactNumber = providers[0].contactNumber;
                // Add to our cache
                this.providerDetailsMap[provider] = providers[0];
              }
            }
          });
        }
      }
    } else {
      // No provider selected, clear validators
      this.rsaProvidedForm.get('reimbursementAmount').clearValidators();
      this.rsaProvidedForm.get('reimbursementComment').clearValidators();
    }
    
    // Update form control validity
    this.rsaProvidedForm.get('reimbursementAmount').updateValueAndValidity();
    this.rsaProvidedForm.get('reimbursementComment').updateValueAndValidity();
  }

  onStateChange(state: string) {
    const found = this.stateCityList.find(x => x.state === state);
    this.cityList = found ? found.city : [];
    
    // Reset city selection when state changes
    this.rsaProvidedForm.patchValue({ city: '' }, { emitEvent: false });
    
    // Reset provider selections
    this.rsaProvidedForm.patchValue({ provider: '' }, { emitEvent: false });
    this.selectedProviderContactNumber = '';
    
    // Reset and refresh provider list based on the new state
    this.providerList = [];
    this.providerDetailsMap = {};
    
    // Load providers for the selected state
    if (state) {
      this.loading = true;
      this.configDataService.getRsaProviderByState(state).pipe(first()).subscribe({
        next: (stateProviders) => {
          if (stateProviders && stateProviders.length > 0) {
            // Cache provider details for quick lookup later
            stateProviders.forEach(p => {
              const displayName = `${p.providerName} (${p.city})`;
              this.providerDetailsMap[displayName] = p;
            });
            this.providerList = stateProviders.map(p => `${p.providerName} (${p.city})`);
            
            // Add a log to debug provider list updates
            console.log(`Loaded ${this.providerList.length} providers for state: ${state}`);
          } else {
            console.log(`No providers found for state: ${state}`);
          }
          this.loading = false;
        },
        error: (error) => {
          this.providerList = [];
          this.providerDetailsMap = {};
          this.loading = false;
          console.error(`Error loading providers for state ${state}:`, error);
          this.alertService.error('Could not load providers for the selected state. Please try again.');
        }
      });
    }
  }
onCityChange(city: string) {
    if (!city) {
      // If city is cleared, fallback to state-based providers
      const state = this.rsaProvidedForm.get('state').value;
      if (state) {
        this.onStateChange(state);
      } else {
        this.providerList = [];
        this.providerDetailsMap = {};
      }
      return;
    }
    
    // Reset provider selections but prevent triggering additional events
    this.rsaProvidedForm.patchValue({ provider: '' }, { emitEvent: false });
    this.selectedProviderContactNumber = '';
    
    // Reset and refresh provider list based on the new city
    this.providerList = [];
    this.providerDetailsMap = {};
    
    // Load providers for the selected city
    this.loading = true;
    console.log(`Loading providers for city: ${city}`);
    
    this.configDataService.getRsaProviderByCity(city).pipe(first()).subscribe({
      next: (cityProviders) => {
        if (cityProviders && cityProviders.length > 0) {
          // Cache provider details for quick lookup later
          cityProviders.forEach(p => {
            const displayName = `${p.providerName} (${p.city})`;
            this.providerDetailsMap[displayName] = p;
          });
          this.providerList = cityProviders.map(p => `${p.providerName} (${p.city})`);
          console.log(`Loaded ${this.providerList.length} providers for city: ${city}`);
        } else {
          console.log(`No providers found for city: ${city}, falling back to state providers`);
          // If no city-specific providers, try loading from state
          const state = this.rsaProvidedForm.get('state').value;
          if (state) {
            console.log(`Trying to load providers for state: ${state}`);
            this.configDataService.getRsaProviderByState(state).pipe(first()).subscribe({
              next: (stateProviders) => {
                if (stateProviders && stateProviders.length > 0) {
                  stateProviders.forEach(p => {
                    const displayName = `${p.providerName} (${p.city})`;
                    this.providerDetailsMap[displayName] = p;
                  });
                  this.providerList = stateProviders.map(p => `${p.providerName} (${p.city})`);
                  console.log(`Loaded ${this.providerList.length} state-level providers as fallback`);
                } else {
                  console.log(`No providers found for state: ${state} either`);
                }
                this.loading = false;
              },
              error: (stateError) => {
                console.error(`Error loading providers for state ${state}:`, stateError);
                this.providerList = [];
                this.loading = false;
              }
            });
          } else {
            console.log('No state selected, cannot fall back to state providers');
            this.loading = false;
          }
        }
        this.loading = false;
      },
      error: (error) => {
        this.providerList = [];
        this.loading = false;
        console.error(`Error loading providers for city ${city}:`, error);
        this.alertService.error('Could not load providers for the selected city. Please try again.');
      }
    });
  }
onRsaSubmit() {
    this.submitted = true;
    this.alertService.clear();
    if (this.rsaProvidedForm.invalid) {
      return;
    }
    this.loading = true;
    // Set certificateNumber before submit
    this.rsaProvidedForm.patchValue({ certificateNumber: this.selectedCertificate.certificateNumber });
    const formValue = this.rsaProvidedForm.getRawValue();
    // Add new history entry
    const username = this.accountService.userValue?.username || 'Unknown';
    // Create a Date object with IST timezone
    const now = new Date(this.getISTDateTime());
    const status = formValue.serviceStatus;
    const comment = this.newHistoryCommentControl.value || '';
    const newHistory = { comment, actionedBy: username, modifiedTime: now, modifiedStatus: status };
    formValue.history = Array.isArray(formValue.history) ? [...formValue.history, newHistory] : [newHistory];
    // Attach the full selectedCertificate object
    formValue.certificate = this.selectedCertificate;
    // Attach full provider object if not 'Other'
    if (formValue.provider && formValue.provider !== 'Other') {
      // Use the complete provider data from providerDetailsMap if available
      const matchingProvider = this.providerDetailsMap[formValue.provider];
      
      if (matchingProvider) {
        // Use the complete provider object with all its properties
        formValue.rsaProvidedBy = matchingProvider;
      } else {
        // Fallback to basic provider information if not found in the map
        const providerName = formValue.provider.split(' (')[0];
        const providerCity = formValue.provider.split(' (')[1]?.replace(')', '') || '';
        formValue.rsaProvidedBy = {
          providerName: providerName,
          city: providerCity
        };
        console.warn(`Complete provider data not found for ${formValue.provider}, using basic information instead`);
      }
    } else {
      formValue.rsaProvidedBy = null;
    }
    this.selectRsaProvide = new RsaProvided(formValue);

    if (this.selectRsaProvide.rsaProvideInstanceNumber == 0) {
      // Use IST date for the service provided date
      this.selectRsaProvide.rsaProvidedDate = new Date(this.getISTDateTime());
      this.selectRsaProvide.rsaProvideInstanceNumber = (this.selectedCertificate.rsaProvidedInfo?.length || 0) + 1;
    }
    // Send only the new RsaProvided object to the backend
    this.certificateService.rsaProvideCertificate(this.selectRsaProvide)
      .pipe(first())
      .subscribe({
        next: () => {
          this.alertService.success("RSA Provided Updated successfully on certificate Number " + this.selectedCertificate.certificateNumber);
          this.loading = false;
          this.submitted = false;
          this.rsaProvidedForm.reset();
          this.selectedProviderContactNumber = ''; // Reset provider contact number
          this.showRsaForm = false;
        },
        error: error => {
          this.alertService.error(error);
          this.loading = false;
          this.submitted = false;
        }
      });
    // Clear comment after submit
    this.newHistoryCommentControl.reset();
  }
  // Add method to update status to Completed and add history
  addRequestHistory(rsaProvide: RsaProvided) {
    const comment = prompt('Enter comment for this request history:');
    if (!comment) return;
    this.loading = true;
    // Try to get id from any property (id, _id, or certificateNumber as fallback)
    const rsaProvidedId = (rsaProvide as any).id || (rsaProvide as any)._id || rsaProvide.certificateNumber;
    
    // Update the service data with current IST time
    this.rsaServiceData.updateRsaServiceProvidedHistory(rsaProvidedId, 'Completed', comment)
      .pipe(first())
      .subscribe({
        next: (updated) => {
          this.alertService.success('Request history updated successfully.');
          this.loadRsaProvidedAndValidate();
          this.loading = false;
        },
        error: error => {
          this.alertService.error(error);
          this.loading = false;
        }
      });
  }

  // Helper method to copy text to clipboard
  copyToClipboard(text: string) {
    if (!text) return;
    
    // Create a temporary element for copying
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    
    // Execute copy command
    document.execCommand('copy');
    
    // Clean up
    document.body.removeChild(el);
    
    // Show feedback to the user
    this.alertService.success('Contact number copied to clipboard!');
  }

  // Auto-fill location data from certificate
  autoFillLocation() {
    if (this.selectedCertificate) {
      this.rsaProvidedForm.patchValue({
        state: this.selectedCertificate.state || '',
        city: this.selectedCertificate.city || ''
      });

      // Update city dropdown if state is selected
      if (this.selectedCertificate.state) {
        this.onStateChange(this.selectedCertificate.state);
      }

      this.alertService.success('Location information auto-filled');
    }
  }

  // Copy customer details from certificate
  copyCustomerInfo() {
    if (this.selectedCertificate) {
      this.rsaProvidedForm.patchValue({
        callerName: ((this.selectedCertificate.firstName || '') + ' ' + (this.selectedCertificate.lastName || '')).trim(),
        callbackNumber: this.selectedCertificate.contactNumber || '',
        state: this.selectedCertificate.state || '',
        city: this.selectedCertificate.city || ''
      });

      // Update city dropdown if state is selected
      if (this.selectedCertificate.state) {
        this.onStateChange(this.selectedCertificate.state);
      }

      this.alertService.success('Customer information copied from certificate');
    }
  }

  // Cancel RSA form
  cancelRsaForm() {
    this.showRsaForm = false;
    this.submitted = false;
    this.selectedProviderContactNumber = '';
    this.newHistoryCommentControl.reset();
    this.alertService.clear();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.stateChangeSubscription) {
      this.stateChangeSubscription.unsubscribe();
    }
    if (this.cityChangeSubscription) {
      this.cityChangeSubscription.unsubscribe();
    }
  }
}
